/**
 * NRC (Norwegian Refugee Council) Operating Locations
 * Data sourced from nrc.no/where-we-work
 *
 * NRC works in 40+ countries across Africa, Asia, Europe, the Middle East, and the Americas
 */

export interface NRCLocation {
  id: string;
  country: string;
  region: NRCRegion;
  subRegion?: string;
  capital: string;
  latitude: number;
  longitude: number;
  operationTypes: NRCOperationType[];
  staffCount?: number;
  yearEstablished?: number;
  description?: string;
  isActive: boolean;
}

export type NRCRegion =
  | 'East Africa and Yemen'
  | 'Central and West Africa'
  | 'Southern Africa'
  | 'Middle East'
  | 'Asia'
  | 'Europe'
  | 'Americas'
  | 'Representation Office';

export type NRCOperationType =
  | 'Education'
  | 'Food Security'
  | 'Legal Assistance (ICLA)'
  | 'Shelter and Settlements'
  | 'WASH'
  | 'Camp Management'
  | 'Protection'
  | 'Livelihoods'
  | 'Cash Assistance';

// NRC Core Competencies
export const nrcCoreCompetencies: { id: NRCOperationType; label: string; description: string }[] = [
  { id: 'Education', label: 'Education', description: 'Ensuring displaced children have access to quality education' },
  { id: 'Food Security', label: 'Food Security', description: 'Addressing hunger and food insecurity in crisis contexts' },
  { id: 'Legal Assistance (ICLA)', label: 'Information, Counselling & Legal Assistance', description: 'Helping displaced people obtain documentation and exercise their rights' },
  { id: 'Shelter and Settlements', label: 'Shelter and Settlements', description: 'Providing safe and dignified housing solutions' },
  { id: 'WASH', label: 'Water, Sanitation & Hygiene', description: 'Ensuring access to clean water and sanitation facilities' },
  { id: 'Camp Management', label: 'Camp Management', description: 'Coordinating services in displacement camps and sites' },
];

// NRC Operating Locations
export const nrcLocations: NRCLocation[] = [
  // ==================== EAST AFRICA AND YEMEN ====================
  {
    id: 'djibouti',
    country: 'Djibouti',
    region: 'East Africa and Yemen',
    capital: 'Djibouti City',
    latitude: 11.5886,
    longitude: 43.1456,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education'],
    isActive: true,
    description: 'Supporting refugees and migrants at the crossroads of Africa and the Middle East'
  },
  {
    id: 'eritrea',
    country: 'Eritrea',
    region: 'East Africa and Yemen',
    capital: 'Asmara',
    latitude: 15.3229,
    longitude: 38.9251,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection'],
    isActive: true,
    description: 'Providing protection and legal assistance to vulnerable populations'
  },
  {
    id: 'ethiopia',
    country: 'Ethiopia',
    region: 'East Africa and Yemen',
    capital: 'Addis Ababa',
    latitude: 9.0320,
    longitude: 38.7469,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Legal Assistance (ICLA)'],
    isActive: true,
    yearEstablished: 2011,
    description: 'Responding to displacement caused by conflict and climate emergencies across multiple regions'
  },
  {
    id: 'kenya',
    country: 'Kenya',
    region: 'East Africa and Yemen',
    capital: 'Nairobi',
    latitude: -1.2921,
    longitude: 36.8219,
    operationTypes: ['Education', 'Legal Assistance (ICLA)', 'Shelter and Settlements', 'Livelihoods'],
    isActive: true,
    description: 'Supporting refugees in Dadaab and Kakuma camps, as well as urban refugees in Nairobi'
  },
  {
    id: 'somalia',
    country: 'Somalia',
    region: 'East Africa and Yemen',
    capital: 'Mogadishu',
    latitude: 2.0469,
    longitude: 45.3182,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Camp Management'],
    isActive: true,
    yearEstablished: 2004,
    description: 'One of NRC\'s largest operations, responding to conflict and climate displacement'
  },
  {
    id: 'south-sudan',
    country: 'South Sudan',
    region: 'East Africa and Yemen',
    capital: 'Juba',
    latitude: 4.8594,
    longitude: 31.5713,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Camp Management', 'Legal Assistance (ICLA)'],
    isActive: true,
    yearEstablished: 2004,
    description: 'Comprehensive humanitarian response across multiple states including Aweil, Wau, Bor, and Juba'
  },
  {
    id: 'tanzania',
    country: 'Tanzania',
    region: 'East Africa and Yemen',
    capital: 'Dodoma',
    latitude: -6.1630,
    longitude: 35.7516,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education'],
    isActive: true,
    yearEstablished: 2015,
    description: 'Supporting Burundian and Congolese refugees in Mtendeli, Nduta, and Nyarugusu camps'
  },
  {
    id: 'uganda',
    country: 'Uganda',
    region: 'East Africa and Yemen',
    capital: 'Kampala',
    latitude: 0.3476,
    longitude: 32.5825,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Legal Assistance (ICLA)', 'Livelihoods'],
    isActive: true,
    description: 'Supporting one of Africa\'s largest refugee populations from South Sudan and DRC'
  },
  {
    id: 'yemen',
    country: 'Yemen',
    region: 'East Africa and Yemen',
    capital: 'Sana\'a',
    latitude: 15.3694,
    longitude: 44.1910,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Legal Assistance (ICLA)'],
    isActive: true,
    yearEstablished: 2009,
    description: 'Responding to one of the world\'s worst humanitarian crises with multi-sector assistance'
  },

  // ==================== CENTRAL AND WEST AFRICA ====================
  {
    id: 'burkina-faso',
    country: 'Burkina Faso',
    region: 'Central and West Africa',
    capital: 'Ouagadougou',
    latitude: 12.2383,
    longitude: -1.5616,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security'],
    isActive: true,
    yearEstablished: 2019,
    description: 'Responding to the rapidly growing displacement crisis in the Sahel region'
  },
  {
    id: 'cameroon',
    country: 'Cameroon',
    region: 'Central and West Africa',
    capital: 'Yaoundé',
    latitude: 3.8480,
    longitude: 11.5021,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Legal Assistance (ICLA)'],
    isActive: true,
    description: 'Supporting refugees from CAR and Nigeria, and IDPs in Anglophone regions'
  },
  {
    id: 'car',
    country: 'Central African Republic',
    region: 'Central and West Africa',
    capital: 'Bangui',
    latitude: 4.3947,
    longitude: 18.5582,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Camp Management'],
    isActive: true,
    yearEstablished: 2014,
    description: 'Providing humanitarian assistance to communities affected by protracted conflict'
  },
  {
    id: 'chad',
    country: 'Chad',
    region: 'Central and West Africa',
    capital: 'N\'Djamena',
    latitude: 12.1348,
    longitude: 15.0557,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security'],
    isActive: true,
    description: 'Supporting Sudanese refugees and host communities affected by regional crises'
  },
  {
    id: 'drc',
    country: 'Democratic Republic of the Congo',
    region: 'Central and West Africa',
    capital: 'Kinshasa',
    latitude: -4.4419,
    longitude: 15.2663,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Camp Management', 'Protection'],
    isActive: true,
    yearEstablished: 2001,
    description: 'Responding to complex displacement in North Kivu, South Kivu, Ituri, and Tanganyika'
  },
  {
    id: 'mali',
    country: 'Mali',
    region: 'Central and West Africa',
    capital: 'Bamako',
    latitude: 12.6392,
    longitude: -8.0029,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Legal Assistance (ICLA)'],
    isActive: true,
    description: 'Supporting displaced populations affected by conflict in the Sahel'
  },
  {
    id: 'niger',
    country: 'Niger',
    region: 'Central and West Africa',
    capital: 'Niamey',
    latitude: 13.5137,
    longitude: 2.1098,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security'],
    isActive: true,
    yearEstablished: 2019,
    description: 'Responding to displacement from Mali, Nigeria, and internal conflicts'
  },
  {
    id: 'nigeria',
    country: 'Nigeria',
    region: 'Central and West Africa',
    capital: 'Abuja',
    latitude: 9.0765,
    longitude: 7.3986,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Legal Assistance (ICLA)', 'Livelihoods'],
    isActive: true,
    yearEstablished: 2015,
    description: 'Supporting millions displaced by conflict in Borno, Adamawa, and Yobe states'
  },

  // ==================== SOUTHERN AFRICA ====================
  {
    id: 'libya',
    country: 'Libya',
    region: 'Southern Africa',
    subRegion: 'North Africa',
    capital: 'Tripoli',
    latitude: 32.8872,
    longitude: 13.1913,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'WASH'],
    isActive: true,
    description: 'Providing protection and assistance to migrants, refugees, and displaced Libyans'
  },
  {
    id: 'sudan',
    country: 'Sudan',
    region: 'Southern Africa',
    subRegion: 'North Africa',
    capital: 'Khartoum',
    latitude: 15.5007,
    longitude: 32.5599,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Camp Management'],
    isActive: true,
    description: 'Responding to the acute crisis following the 2023 conflict, with operations in multiple states'
  },
  {
    id: 'mozambique',
    country: 'Mozambique',
    region: 'Southern Africa',
    capital: 'Maputo',
    latitude: -25.9692,
    longitude: 32.5732,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security'],
    isActive: true,
    description: 'Supporting communities affected by conflict in Cabo Delgado and climate disasters'
  },

  // ==================== MIDDLE EAST ====================
  {
    id: 'iraq',
    country: 'Iraq',
    region: 'Middle East',
    capital: 'Baghdad',
    latitude: 33.3128,
    longitude: 44.3615,
    operationTypes: ['Shelter and Settlements', 'Education', 'Legal Assistance (ICLA)', 'Camp Management', 'Livelihoods'],
    isActive: true,
    yearEstablished: 2010,
    description: 'Supporting returnees, IDPs, and refugees with durable solutions and recovery assistance'
  },
  {
    id: 'jordan',
    country: 'Jordan',
    region: 'Middle East',
    capital: 'Amman',
    latitude: 31.9454,
    longitude: 35.9284,
    operationTypes: ['Education', 'Legal Assistance (ICLA)', 'Shelter and Settlements', 'Livelihoods'],
    isActive: true,
    yearEstablished: 2012,
    description: 'Supporting Syrian refugees and vulnerable Jordanian communities'
  },
  {
    id: 'lebanon',
    country: 'Lebanon',
    region: 'Middle East',
    capital: 'Beirut',
    latitude: 33.8938,
    longitude: 35.5018,
    operationTypes: ['Education', 'Legal Assistance (ICLA)', 'Shelter and Settlements', 'Livelihoods'],
    isActive: true,
    yearEstablished: 2006,
    description: 'One of NRC\'s largest operations, supporting Syrian and Palestinian refugees'
  },
  {
    id: 'palestine',
    country: 'Palestine',
    region: 'Middle East',
    capital: 'Ramallah',
    latitude: 31.9038,
    longitude: 35.2034,
    operationTypes: ['Legal Assistance (ICLA)', 'Shelter and Settlements', 'Education', 'WASH'],
    isActive: true,
    yearEstablished: 2009,
    description: 'Providing humanitarian assistance in the West Bank and Gaza Strip'
  },
  {
    id: 'syria',
    country: 'Syria',
    region: 'Middle East',
    capital: 'Damascus',
    latitude: 33.5138,
    longitude: 36.2765,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Food Security', 'Legal Assistance (ICLA)', 'Livelihoods'],
    isActive: true,
    yearEstablished: 2016,
    description: 'Providing multi-sector assistance to displaced Syrians and affected communities'
  },

  // ==================== ASIA ====================
  {
    id: 'afghanistan',
    country: 'Afghanistan',
    region: 'Asia',
    capital: 'Kabul',
    latitude: 34.5553,
    longitude: 69.2075,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Legal Assistance (ICLA)', 'Food Security'],
    isActive: true,
    yearEstablished: 2003,
    description: 'One of the largest humanitarian operations, serving millions affected by conflict and crisis'
  },
  {
    id: 'bangladesh',
    country: 'Bangladesh',
    region: 'Asia',
    capital: 'Dhaka',
    latitude: 23.8103,
    longitude: 90.4125,
    operationTypes: ['Shelter and Settlements', 'Education', 'Legal Assistance (ICLA)', 'Camp Management', 'WASH'],
    isActive: true,
    yearEstablished: 2017,
    description: 'Supporting Rohingya refugees in Cox\'s Bazar, the world\'s largest refugee camp'
  },
  {
    id: 'iran',
    country: 'Iran',
    region: 'Asia',
    capital: 'Tehran',
    latitude: 35.6892,
    longitude: 51.3890,
    operationTypes: ['Legal Assistance (ICLA)', 'Education', 'Livelihoods'],
    isActive: true,
    description: 'Supporting Afghan refugees with education and legal assistance'
  },
  {
    id: 'myanmar',
    country: 'Myanmar',
    region: 'Asia',
    capital: 'Naypyidaw',
    latitude: 19.7633,
    longitude: 96.0785,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Education', 'Camp Management', 'Protection'],
    isActive: true,
    description: 'Responding to complex displacement across Rakhine, Kachin, and other states'
  },

  // ==================== EUROPE ====================
  {
    id: 'moldova',
    country: 'Moldova',
    region: 'Europe',
    capital: 'Chișinău',
    latitude: 47.0105,
    longitude: 28.8638,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'Cash Assistance'],
    isActive: true,
    yearEstablished: 2022,
    description: 'Supporting Ukrainian refugees with legal assistance and social support'
  },
  {
    id: 'poland',
    country: 'Poland',
    region: 'Europe',
    capital: 'Warsaw',
    latitude: 52.2297,
    longitude: 21.0122,
    operationTypes: ['Legal Assistance (ICLA)', 'Education', 'Protection', 'Cash Assistance'],
    isActive: true,
    yearEstablished: 2022,
    description: 'Supporting Ukrainian refugees with integration, education, and legal assistance'
  },
  {
    id: 'romania',
    country: 'Romania',
    region: 'Europe',
    capital: 'Bucharest',
    latitude: 44.4268,
    longitude: 26.1025,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'Cash Assistance'],
    isActive: true,
    yearEstablished: 2022,
    description: 'Providing humanitarian assistance to Ukrainian refugees'
  },
  {
    id: 'ukraine',
    country: 'Ukraine',
    region: 'Europe',
    capital: 'Kyiv',
    latitude: 50.4501,
    longitude: 30.5234,
    operationTypes: ['Shelter and Settlements', 'WASH', 'Legal Assistance (ICLA)', 'Cash Assistance', 'Protection', 'Food Security'],
    isActive: true,
    yearEstablished: 2014,
    description: 'Major emergency response supporting millions displaced by the ongoing conflict'
  },

  // ==================== AMERICAS ====================
  {
    id: 'colombia',
    country: 'Colombia',
    region: 'Americas',
    capital: 'Bogotá',
    latitude: 4.7110,
    longitude: -74.0721,
    operationTypes: ['Legal Assistance (ICLA)', 'Shelter and Settlements', 'Education', 'Livelihoods'],
    isActive: true,
    yearEstablished: 1999,
    description: 'Supporting displaced Colombians and Venezuelan refugees across multiple regions'
  },
  {
    id: 'ecuador',
    country: 'Ecuador',
    region: 'Americas',
    capital: 'Quito',
    latitude: -0.1807,
    longitude: -78.4678,
    operationTypes: ['Legal Assistance (ICLA)', 'Shelter and Settlements', 'Livelihoods'],
    isActive: true,
    description: 'Supporting Venezuelan refugees and migrants with protection and assistance'
  },
  {
    id: 'el-salvador',
    country: 'El Salvador',
    region: 'Americas',
    capital: 'San Salvador',
    latitude: 13.6929,
    longitude: -89.2182,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection'],
    isActive: true,
    description: 'Providing protection and legal assistance to vulnerable populations'
  },
  {
    id: 'guatemala',
    country: 'Guatemala',
    region: 'Americas',
    capital: 'Guatemala City',
    latitude: 14.6349,
    longitude: -90.5069,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'Shelter and Settlements'],
    isActive: true,
    description: 'Supporting displaced populations and deportees with protection services'
  },
  {
    id: 'honduras',
    country: 'Honduras',
    region: 'Americas',
    capital: 'Tegucigalpa',
    latitude: 14.0723,
    longitude: -87.1921,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'Shelter and Settlements'],
    isActive: true,
    description: 'Providing humanitarian assistance to vulnerable communities and displaced persons'
  },
  {
    id: 'mexico',
    country: 'Mexico',
    region: 'Americas',
    capital: 'Mexico City',
    latitude: 19.4326,
    longitude: -99.1332,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'Shelter and Settlements'],
    isActive: true,
    description: 'Supporting migrants, refugees, and asylum seekers along migration routes'
  },
  {
    id: 'panama',
    country: 'Panama',
    region: 'Americas',
    capital: 'Panama City',
    latitude: 9.1012,
    longitude: -79.4029,
    operationTypes: ['Legal Assistance (ICLA)', 'Protection', 'WASH'],
    isActive: true,
    description: 'Providing humanitarian assistance to migrants crossing the Darién Gap'
  },
  {
    id: 'venezuela',
    country: 'Venezuela',
    region: 'Americas',
    capital: 'Caracas',
    latitude: 10.4806,
    longitude: -66.9036,
    operationTypes: ['Food Security', 'Shelter and Settlements', 'WASH', 'Livelihoods'],
    isActive: true,
    description: 'Supporting vulnerable communities with food security and livelihoods assistance'
  },

  // ==================== REPRESENTATION OFFICES ====================
  {
    id: 'belgium',
    country: 'Belgium',
    region: 'Representation Office',
    capital: 'Brussels',
    latitude: 50.8503,
    longitude: 4.3517,
    operationTypes: [],
    isActive: true,
    description: 'NRC EU Representation Office for advocacy with European Union institutions'
  },
  {
    id: 'switzerland',
    country: 'Switzerland',
    region: 'Representation Office',
    capital: 'Geneva',
    latitude: 46.2044,
    longitude: 6.1432,
    operationTypes: [],
    isActive: true,
    description: 'NRC Geneva Office for UN coordination and humanitarian policy'
  },
  {
    id: 'usa',
    country: 'United States',
    region: 'Representation Office',
    capital: 'Washington D.C.',
    latitude: 38.9072,
    longitude: -77.0369,
    operationTypes: [],
    isActive: true,
    description: 'NRC USA Office for advocacy and partnership with US government'
  },
  {
    id: 'germany',
    country: 'Germany',
    region: 'Representation Office',
    capital: 'Berlin',
    latitude: 52.5200,
    longitude: 13.4050,
    operationTypes: [],
    isActive: true,
    description: 'NRC Germany Office for advocacy and donor relations'
  },
  {
    id: 'uk',
    country: 'United Kingdom',
    region: 'Representation Office',
    capital: 'London',
    latitude: 51.5074,
    longitude: -0.1278,
    operationTypes: [],
    isActive: true,
    description: 'NRC UK Office for advocacy and partnership development'
  }
];

// Region colors for visualization
export const regionColors: Record<NRCRegion, string> = {
  'East Africa and Yemen': '#FF6B35',
  'Central and West Africa': '#F7C948',
  'Southern Africa': '#E53E3E',
  'Middle East': '#3182CE',
  'Asia': '#48BB78',
  'Europe': '#805AD5',
  'Americas': '#ED8936',
  'Representation Office': '#718096'
};

// Region icons (using Swiss icon keys)
export const regionIcons: Record<NRCRegion, string> = {
  'East Africa and Yemen': 'globe',
  'Central and West Africa': 'globe',
  'Southern Africa': 'globe',
  'Middle East': 'globe',
  'Asia': 'globe',
  'Europe': 'globe',
  'Americas': 'globe',
  'Representation Office': 'workspace'
};

// Helper functions
export const getLocationsByRegion = (region: NRCRegion): NRCLocation[] => {
  return nrcLocations.filter(loc => loc.region === region);
};

export const getActiveLocations = (): NRCLocation[] => {
  return nrcLocations.filter(loc => loc.isActive);
};

export const getOperationalLocations = (): NRCLocation[] => {
  return nrcLocations.filter(loc => loc.region !== 'Representation Office' && loc.isActive);
};

export const getRepresentationOffices = (): NRCLocation[] => {
  return nrcLocations.filter(loc => loc.region === 'Representation Office');
};

export const getLocationsByOperationType = (type: NRCOperationType): NRCLocation[] => {
  return nrcLocations.filter(loc => loc.operationTypes.includes(type));
};

export const getAllRegions = (): NRCRegion[] => {
  return [...new Set(nrcLocations.map(loc => loc.region))];
};

// Statistics
export const nrcStatistics = {
  totalCountries: nrcLocations.filter(loc => loc.region !== 'Representation Office').length,
  totalLocations: nrcLocations.length,
  activeOperations: nrcLocations.filter(loc => loc.isActive && loc.region !== 'Representation Office').length,
  representationOffices: nrcLocations.filter(loc => loc.region === 'Representation Office').length,
  regionCount: getAllRegions().length - 1, // Exclude representation offices
  staffCount: 16500, // Approximate
  yearFounded: 1946
};
